import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface PageHeaderProps {
  /** Page title */
  title: string
  /** Optional description text or element */
  description?: React.ReactNode
  /** Back link URL (shows back button if provided) */
  backLink?: string
  /** Back link label */
  backLabel?: string
  /** Action buttons to display on the right */
  actions?: React.ReactNode
  /** Inline content rendered next to the title (e.g. stats pills) */
  inlineContent?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

/**
 * Page header with title, optional back link, and action buttons.
 * 
 * @example
 * ```tsx
 * <PageHeader 
 *   title="Dashboard" 
 *   actions={<Button>+ New Run</Button>} 
 * />
 * 
 * <PageHeader
 *   title="Run Details"
 *   backLink="/runs"
 *   backLabel="Back to Runs"
 * />
 * ```
 */
export function PageHeader({
  title,
  description,
  backLink,
  backLabel = 'Back',
  actions,
  inlineContent,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {backLink && (
        <Link
          to={backLink}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap min-w-0">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            {description && (
              <div className="text-muted-foreground mt-1">{description}</div>
            )}
          </div>
          {inlineContent}
        </div>
        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </div>
    </div>
  )
}
